import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChevronDown, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { calculatePricing } from '../services/pricingService';

const SidingCalculatorPanel = forwardRef(({
  isOpen, onToggle, measurementTotals, manualOverride, setManualOverride,
  onServiceTotalChange, initialInputs, onInputsChange,
}, ref) => {
  const [sidingData, setSidingData] = useState(
    initialInputs && Object.keys(initialInputs).length
      ? { sqFt: '', material: 'vinyl', stories: '1', ...initialInputs }
      : { sqFt: '', material: 'vinyl', stories: '1' }
  );

  const onInputsChangeRef = useRef(onInputsChange);
  onInputsChangeRef.current = onInputsChange;
  useEffect(() => { onInputsChangeRef.current?.('siding', sidingData); }, [sidingData]);

  // Auto-fill from measurement if panel is open and user hasn't manually edited
  useEffect(() => {
    if (!isOpen) return;
    const area = measurementTotals?.areaSqFt || 0;
    // Only auto-fill if: area > 0 AND user hasn't manually overridden
    if (area > 0 && !manualOverride.sidingSqFt) {
      // Round to whole number for clean UX display
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional sync from external measurement source
      setSidingData((prev) => ({ ...prev, sqFt: Math.round(area) }));
    }
  }, [isOpen, measurementTotals?.areaSqFt, manualOverride.sidingSqFt]);

  const handleCalculate = async () => {
    const sq = Number(sidingData.sqFt);
    if (!Number.isFinite(sq) || sq <= 0) {
      toast.error('Please enter siding square footage.');
      return;
    }

    const resp = await calculatePricing('siding', {
      data: {
        sqFt: sq,
        material: sidingData.material,
        stories: sidingData.stories,
      },
      discount: 0,
    });

    if (!resp.success || !resp.quote) {
      toast.error(resp.message || 'Service pricing not implemented yet, but UI remains active.');
      onServiceTotalChange('siding', { subtotal: 0, tax: 0, grandTotal: 0 });
      return;
    }

    const quote = resp.quote;
    const nowIso = new Date().toISOString();

    onServiceTotalChange('siding', {
      input: {
        sqFt: sq,
        material: sidingData.material,
        stories: sidingData.stories,
      },
      result: {
        serviceTotal: quote.serviceTotal || quote.subtotal || 0,
        adjustedSqFt: quote.adjustedSqFt || sq,
        discount: quote.discount || 0,
        tax: quote.tax || 0,
        subtotal: quote.subtotal || quote.serviceTotal || 0,
        grandTotal: quote.grandTotal || 0,
      },
      measurement: {
        polygons: null,
        linearFeet: null,
        sqFt: sq,
        computedAt: nowIso,
      },
    });
  };

  useImperativeHandle(ref, () => ({
    recalc: handleCalculate,
    getCurrentInputs: () => sidingData,
  }), [sidingData]);

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <Card className="overflow-hidden">
        <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-secondary/10 rounded-lg">
              <Building2 className="h-5 w-5 text-secondary" />
            </div>
            <div className="text-left">
              <h3 className="text-lg font-bold text-secondary">🧱 Siding Cleaning</h3>
              <p className="text-sm text-muted-foreground">Configure siding specifications</p>
            </div>
          </div>
          <ChevronDown
            className={`h-5 w-5 text-muted-foreground transition-transform ${
              isOpen ? 'transform rotate-180' : ''
            }`}
          />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="p-6 space-y-4 bg-muted/30">
            {/* Measured Area */}
            <div className="space-y-2">
              <Label htmlFor="siding-area">Measured Area (sq ft)</Label>
              <Input
                id="siding-area"
                type="number"
                placeholder="Enter square footage"
                className="font-mono"
                value={sidingData.sqFt}
                onChange={(e) => {
                  setManualOverride(prev => ({ ...prev, sidingSqFt: true }));
                  setSidingData(prev => ({ ...prev, sqFt: e.target.value }));
                }}
              />
            </div>

            {/* Material Type */}
            <div className="space-y-2">
              <Label htmlFor="siding-material">Material / Surface Type</Label>
              <Select value={sidingData.material} onValueChange={(value) => setSidingData(prev => ({ ...prev, material: value }))}>
                <SelectTrigger id="siding-material">
                  <SelectValue placeholder="Select material" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vinyl">Vinyl</SelectItem>
                  <SelectItem value="hardie">Hardie</SelectItem>
                  <SelectItem value="wood">Wood</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Story Count */}
            <div className="space-y-2">
              <Label htmlFor="siding-stories">Story Count</Label>
              <Select
                value={sidingData.stories}
                onValueChange={(value) => setSidingData(prev => ({ ...prev, stories: value }))}
              >
                <SelectTrigger id="siding-stories">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">One Story</SelectItem>
                  <SelectItem value="2">Two Story</SelectItem>
                  <SelectItem value="3">Three Story</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Calculate Button */}
            <Button
              className="w-full bg-primary hover:bg-primary/90"
              size="lg"
              onClick={handleCalculate}
            >
              Calculate Siding Cost
            </Button>
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
});

export default SidingCalculatorPanel;
